import React, { memo, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { exercisesList, exerciseCategoriesList } from '../../services/exercises';

import { useLocalStorage } from 'usehooks-ts';
import ExerciseCanvas from '../../components/exercise-canvas';
import Pagination from '../../components/shared/pagination';
import { SortBar, SearchBar } from '../../components/FilterBar';
import FiltersModalButton from '../../components/FiltersModalButton';

export const ExerciseListPage = () => {
  const navigate = useNavigate();

  const add = () => navigate('/club_admin/exercises/new');
  const select = item => navigate(`/club_admin/exercises/${item.id}`);

  return <ExercisesList select={select} add={add} />;
};

export default memo(ExerciseListPage);

export const ExercisesList = ({ select, add, listSize = 188, cardSize = 350 }) => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [exercises, setExercises] = useState([]);
  const [categories, setCategories] = useState([]);
  const [count, setCount] = useState(0);

  const getFilterValue = (param, defaultValue) => searchParams.get(param) || defaultValue;
  const initialFilters = {
    page: Number(getFilterValue("page", 1)),
    per_page: 10,
    owner_type: getFilterValue("owner_type", "club"),
    sort_dir: getFilterValue("sort_dir", 'asc'),
    sort_by: 'title',
    q: getFilterValue("q", ""),
    category_ids: getFilterValue("category_ids", null),
    from_age: getFilterValue("from_age", null),
    to_age: getFilterValue("to_age", null)
  };
  const [filters, setFilters] = useState(initialFilters);
  const { owner_type } = filters;

  const [viewType] = useLocalStorage('exercisesViewType', 'list');

  const updateSearchParams = (newFilters) => {
    const updatedParams = new URLSearchParams(searchParams);
    
    Object.entries(newFilters).forEach(([key, value]) =>
      value ? updatedParams.set(key, value) : updatedParams.delete(key)
    );
  
    setSearchParams(updatedParams);
  };

  const loadData = async () => {
    const { exercises, count } = await exercisesList(filters);
    setExercises(exercises);
    setCount(count);
  };

  useEffect(() => {
    updateSearchParams(filters);
    loadData();
  }, [filters]);


  useEffect(() => { window.scrollTo({ top: 0, behavior: 'smooth' }) }, [filters?.page]);

  useEffect(() => {
    const loadCategories = async () => {
      const data = await exerciseCategoriesList();
      setCategories(data.categories);
    };

    loadCategories();
  }, []);

  return (
    <div className="teams">
      <div className="teams-header">
        <div className="teams-header-text">{t('exercises.exercises')}</div>
        {add && (
          <button className="button button-add" onClick={add}>
            {t('buttons.exercise.add')}
          </button>
        )}
      </div>
      <div className="team-details-tabs margin-top">
        <div
          className={`team-details-tabs-option pointer ${owner_type === 'club' ? 'active' : ''}`}
          onClick={() => owner_type !== 'clubs' && setFilters({ ...filters, owner_type: 'club', page: 1 })}
        >
          {t('exercises.club')}
        </div>
        <div
          className={`team-details-tabs-option pointer ${owner_type === 'supercoach' ? 'active' : ''}`}
          onClick={() => owner_type !== 'supercoach' && setFilters({ ...filters, owner_type: 'supercoach', page: 1 })}
        >
          {t('exercises.supercoach')}
        </div>
      </div>
      <div className="filters-bar">
        <div className="flex1">
          <SortBar viewTypeName="exercisesViewType" onSortChange={sort_dir => setFilters({ ...filters, sort_dir})} direction={filters.sort_dir} />
          <SearchBar onChange={q => { setFilters({ ...filters, q }) }} placeholder={t('exercises.search')} value={filters.q} />
        </div>
        <FiltersModalButton
          filters={filters}
          onChange={params => setFilters({ ...filters, page: 1, ...params })}
          categories={categories}
        />
      </div>

      <div className={`library-list-container ${viewType}-view`}>
        {exercises.map(item =>
          viewType === 'list' ? (
            <ExerciseListItem exercise={item} key={item.id} select={select} size={listSize} />
          ) : (
            <ExerciseCardItem exercise={item} key={item.id} select={select} size={cardSize} />
          )
        )}
      </div>
      <Pagination filters={filters} count={count} onChange={setFilters} />
    </div>
  );
};

const ExerciseListItem = ({ exercise, select, size = 188 }) => {
  const { t } = useTranslation();

  return (
    <div className="library-item">
      <div className="library-item-preview pointer" onClick={() => select(exercise)}>
        <ExerciseCanvas width={size} height={size} exercise={exercise.content}></ExerciseCanvas>
      </div>
      <div className="library-item-info">
        <div className="flex-between1">
          <div className="library-item-info-title pointer" onClick={() => select(exercise)}>{exercise.title}</div>
          <div>
            <span className={`new_label new_label-${exercise.published ? 'success' : 'default'}`}>
              {exercise.published ? t('common.published') : t('common.draft')}
            </span>
          </div>
        </div>
        <div className="library-item-info-row">
          {t('exercises.age')}: {exercise.minimum_age} - {exercise.maximum_age}
        </div>
        <div className="library-item-info-row">
          <div className="tags-container">
            <div className="tags">
              <div className="mr10">{t('exercises.tags')}:</div>
              {exercise.categories.map(category => (
                <button key={category.id} className="button button-category mr10">
                  {category.title}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="library-item-info-row">
          <div className="field-info">
            <img src="/images/icons/field_size.svg" className="field-info-img" />
            {exercise?.field_size}
          </div>
          <div className="field-info">
            <img src="/images/icons/ball.svg" className="field-info-img" />
            {t(`exercises.ball_requirements.${exercise?.required_balls}`, { defaultValue: exercise?.required_balls })}
          </div>
        </div>
      </div>
    </div>
  );
};

const ExerciseCardItem = ({ exercise, select, size = 350 }) => {
  return (
    <div className="library-item">
      <div className="library-item-preview pointer" onClick={() => select(exercise)}>
        <ExerciseCanvas width={size} height={size} exercise={exercise.content}></ExerciseCanvas>
      </div>
      <div className="library-item-info">
        <h5 className="library-item-info-title pointer" onClick={() => select(exercise)}>
          {exercise.title}
        </h5>
      </div>
    </div>
  );
};
